import React, { useEffect, useMemo, useState } from "react";
import { useAuthFetch } from "../hooks/useAuthFetch";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "../stili/StatistichePresenzeStaff.css";
import "../stili/BottoniFinali.css";

const API_URL = process.env.REACT_APP_API_URL;

export default function StatistichePresenzeStaff() {
  const authFetch = useAuthFetch();
  const navigate = useNavigate();

  const [categorie, setCategorie] = useState([]);
  const [idCategoria, setIdCategoria] = useState("");
  const [dataInizio, setDataInizio] = useState("");
  const [dataFine, setDataFine] = useState("");

  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  useEffect(() => {
    authFetch(`${API_URL}/api/staff/presenze/mie-categorie`)
      .then((res) =>
        res.ok ? res.json() : Promise.reject("Errore caricamento categorie staff")
      )
      .then((data) => {
        const lista = Array.isArray(data) ? data : [];
        setCategorie(lista);

        if (lista.length === 1) {
          setIdCategoria(String(lista[0].id));
        }
      })
      .catch((err) => {
        console.error(err);
        setError("Impossibile caricare le categorie dello staff.");
        toast.error("Errore caricamento categorie staff");
      });
  }, [authFetch]);

  useEffect(() => {
    if (!idCategoria || !dataInizio || !dataFine) {
      setStats([]);
      setError("");
      setMessage("");
      return;
    }

    if (dataFine < dataInizio) {
      setStats([]);
      setError("La data fine non può essere precedente alla data inizio.");
      return;
    }

    setLoading(true);
    setError("");
    setMessage("");

    const params = new URLSearchParams({
      idCategoria,
      dataInizio,
      dataFine,
    });

    authFetch(`${API_URL}/api/staff/presenze/statistiche-presenze?${params.toString()}`)
      .then((res) =>
        res.ok ? res.json() : Promise.reject("Errore caricamento statistiche presenze")
      )
      .then((data) => {
        const lista = Array.isArray(data) ? data : [];
        setStats(lista);
        setMessage(
          lista.length > 0
            ? `Trovati ${lista.length} giocatori nel periodo selezionato.`
            : "Nessuna statistica disponibile per i filtri selezionati."
        );
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setError("Errore durante il caricamento delle statistiche.");
        setLoading(false);
      });
  }, [idCategoria, dataInizio, dataFine, authFetch]);

  const handleReset = () => {
    setIdCategoria(categorie.length === 1 ? String(categorie[0].id) : "");
    setDataInizio("");
    setDataFine("");
    setStats([]);
    setError("");
    setMessage("");
  };

  const riepilogo = useMemo(() => {
    const totale = stats.length;

    const media =
      totale > 0
        ? (
            stats.reduce((acc, curr) => acc + Number(curr.percentualePresenza || 0), 0) / totale
          ).toFixed(2)
        : "0.00";

    const sopra80 = stats.filter((g) => Number(g.percentualePresenza || 0) >= 80).length;
    const sotto50 = stats.filter((g) => Number(g.percentualePresenza || 0) < 50).length;

    const migliore =
      totale > 0
        ? [...stats].sort(
            (a, b) => Number(b.percentualePresenza || 0) - Number(a.percentualePresenza || 0)
          )[0]
        : null;

    return {
      totale,
      media,
      sopra80,
      sotto50,
      migliore,
    };
  }, [stats]);

  const categoriaLabel = useMemo(() => {
    const found = categorie.find((c) => String(c.id) === String(idCategoria));
    return found?.nome || "Nessuna categoria selezionata";
  }, [categorie, idCategoria]);

  return (
    <div className="sps-page" translate="no">
      <div className="sps-shell">
        <div className="sps-container">
          <div className="sps-header">
            <h1>Statistiche Presenze</h1>
            <p>Visualizza il report delle tue categorie.</p>
          </div>

          <div className="sps-card">
            <h2>Filtri</h2>

            <div className="sps-filtri-grid">
              <div className="sps-field">
                <label>Categoria</label>
                <select value={idCategoria} onChange={(e) => setIdCategoria(e.target.value)}>
                  <option value="">Seleziona categoria</option>
                  {categorie.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.nome}
                    </option>
                  ))}
                </select>
              </div>

              <div className="sps-field">
                <label>Data inizio</label>
                <input
                  type="date"
                  value={dataInizio}
                  onChange={(e) => setDataInizio(e.target.value)}
                />
              </div>

              <div className="sps-field">
                <label>Data fine</label>
                <input
                  type="date"
                  value={dataFine}
                  min={dataInizio}
                  onChange={(e) => setDataFine(e.target.value)}
                />
              </div>
            </div>

            <div className="sps-actions">
              <button type="button" className="sps-btn secondary" onClick={handleReset}>
                Pulisci filtri
              </button>
            </div>
          </div>

          {(error || message) && (
            <div className={`sps-feedback ${error ? "error" : "success"}`}>
              {error || message}
            </div>
          )}

          <div className="sps-riepilogo">
            <div className="sps-stat-card">
              <span className="sps-stat-label">Categoria</span>
              <span className="sps-stat-text">{categoriaLabel}</span>
            </div>

            <div className="sps-stat-card">
              <span className="sps-stat-label">Giocatori</span>
              <span className="sps-stat-value">{riepilogo.totale}</span>
            </div>

            <div className="sps-stat-card">
              <span className="sps-stat-label">Media presenze</span>
              <span className="sps-stat-value">{riepilogo.media}%</span>
            </div>

            <div className="sps-stat-card">
              <span className="sps-stat-label">Sopra 80%</span>
              <span className="sps-stat-value">{riepilogo.sopra80}</span>
            </div>

            <div className="sps-stat-card">
              <span className="sps-stat-label">Sotto 50%</span>
              <span className="sps-stat-value">{riepilogo.sotto50}</span>
            </div>
          </div>

          <div className="sps-card">
            <div className="sps-risultati-header">
              <div>
                <h2>Risultati</h2>
                <p>
                  {dataInizio && dataFine
                    ? `Periodo: dal ${formatDateIt(dataInizio)} al ${formatDateIt(dataFine)}`
                    : "Seleziona categoria e intervallo date"}
                </p>
              </div>

              {riepilogo.migliore && (
                <div className="sps-highlight">
                  Migliore: {riepilogo.migliore.cognome} {riepilogo.migliore.nome} —{" "}
                  {Number(riepilogo.migliore.percentualePresenza || 0).toFixed(2)}%
                </div>
              )}
            </div>

            {loading ? (
              <div className="sps-state-box">Caricamento statistiche presenze...</div>
            ) : stats.length === 0 ? (
              <div className="sps-state-box">
                Nessuna statistica disponibile per i filtri selezionati.
              </div>
            ) : (
              <div className="sps-list">
                {stats.map((stat) => {
                  const percentuale = Number(stat.percentualePresenza || 0);

                  return (
                    <div className="sps-player-card" key={stat.giocatoreId}>
                      <div className="sps-player-main">
                        <h3>
                          {stat.cognome} {stat.nome}
                        </h3>
                        <div className="sps-player-meta">
                          <span>Categoria: {stat.categoria || "-"}</span>
                        </div>
                      </div>

                      <div className="sps-player-side">
                        <span className={`sps-badge ${getBadgeClass(percentuale)}`}>
                          {percentuale.toFixed(2)}%
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="form-buttons-bottom">
            <button
              type="button"
              className="btn-indietro"
              onClick={() => navigate("/gestionale")}
            >
              Torna al gestionale
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function formatDateIt(value) {
  if (!value) return "";
  const [year, month, day] = value.split("-");
  if (!year || !month || !day) return value;
  return `${day}/${month}/${year}`;
}

function getBadgeClass(percentuale) {
  if (percentuale >= 80) return "alta";
  if (percentuale >= 50) return "media";
  return "bassa";
}